import React from 'react';

import {
  SidePage,
  Section,
  DescriptionList,
  DescriptionItem,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { useScreenGo } from '@gogame/screens';

import Actions from 'scenes/Integrations/components/Actions';
import {
  IntegrationConversionFormEdit,
  IntegrationConversionFormDuplicate,
} from 'scenes/Integrations/components/IntegrationForm';
import { TRAFFIC_TYPE_AFFILIATES } from 'scenes/Integrations/components/IntegrationForm/IntegrationConversionForm/constants';
import { TYPE_CONVERSION } from 'scenes/Integrations/constants';
import {
  useLoadConversionDetails,
  useIsUpdatingConversion,
} from 'scenes/Integrations/data';
import { sentenceCase } from 'scenes/Integrations/utils';

import Metadata from './Metadata';

const IntegrationConversionDetails = ({ id, onClose, onExited, open }) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading, isRefetching } = useLoadConversionDetails(id);
  const isUpdating = useIsUpdatingConversion();
  const go = useScreenGo();

  const handleDuplicate = () => {
    openChildComponent(IntegrationConversionFormDuplicate, {
      id,
      onSubmit: ({ typeId, id }) => {
        go(`${typeId}/${id}`);
      },
    });
  };

  const handleEdit = () => {
    openChildComponent(IntegrationConversionFormEdit, {
      id,
    });
  };

  const handleRemove = () => {
    onClose();
  };

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading}
      fetching={isRefetching || isUpdating}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Integration'}
        entityId={id}
        subTitle={data?.providerType?.name}
      />
      <SidePage.NewBody wrapper={true} dense={true}>
        <SidePage.NewContent withSidebar={true}>
          <Section title={'General'}>
            <DescriptionList
              stripEmpty={true}
              data={data}
              disableGutters={true}
              termWidth={'25%'}
            >
              <DescriptionItem label={'Type'} value={'Conversion'} />
              <DescriptionItem label={'Partner'} field={'providerType.name'} />
              <DescriptionItem label={'Event'} field={'eventType.name'} />
              <DescriptionItem
                label={'Event Platform'}
                field={'platform.name'}
              />
              <DescriptionItem label={'Application'} field={'app.name'} />
              <DescriptionItem label={'Network'} field={'net.name'} />
              <DescriptionItem
                label={'Reg platform'}
                field={'regActionPlatformId.name'}
              />
              <DescriptionItem
                label={'Targeting platform'}
                field={'rePlatformId.name'}
              />
              <DescriptionItem
                label={'Campaigns Ids'}
                field={'adCampaignIds'}
                valueParser={(value) => value?.join(', ')}
              />
              <DescriptionItem
                label={'Registration countries'}
                field={'regActionCountries'}
                type={'typographyList'}
                unitsLabel={'Registration countries'}
                listType={'bullet'}
                placeholder={'All'}
                limit={3}
              />
              <DescriptionItem
                label={'Event / user countries'}
                field={'eventCountries'}
                type={'typographyList'}
                unitsLabel={'Events countries'}
                listType={'bullet'}
                placeholder={'All'}
                limit={3}
              />
              <DescriptionItem
                label={'Excluded channels'}
                field={'excludedAffiliateChannels'}
                valueParser={'commaList'}
              />
              <DescriptionItem
                label={'Traffic'}
                field={'trafficType'}
                valueParser={({ id, name }) =>
                  id === TRAFFIC_TYPE_AFFILIATES
                    ? `${name}: ${
                        data?.affiliates?.map(({ name }) => name)?.join(', ') ||
                        'all'
                      }`
                    : name
                }
              />
              <DescriptionItem
                label={'Activity Type'}
                field={'activityType.name'}
              />
              <DescriptionItem
                label={'Regs'}
                field={'includeRejected'}
                valueParser={(value) =>
                  `${value ? 'Include' : 'Exclude'} rejected`
                }
              />
              <DescriptionItem label={'Sending delay'} field={'sendingDelay'} />
            </DescriptionList>
          </Section>
          <Section title={'Conversion Info'} isCollapsable={true}>
            <DescriptionList
              stripEmpty={true}
              data={data?.conversionInfo}
              disableGutters={true}
              termWidth={'25%'}
            >
              {Object.keys(data?.conversionInfo || {}).map((key) => {
                const label = sentenceCase(key);
                const value = data.conversionInfo[key];

                if (typeof value === 'boolean') {
                  return (
                    <DescriptionItem
                      label={label}
                      value={value ? 'Yes' : 'No'}
                    />
                  );
                }

                if (Array.isArray(value)) {
                  if (!value.length) {
                    return <DescriptionItem label={label} value={'-'} />;
                  }

                  if (typeof value[0] !== 'object') {
                    return (
                      <DescriptionItem
                        label={label}
                        value={value?.join(', ')}
                      />
                    );
                  }

                  return (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          {Object.keys(value[0]).map((key, _i, array) => (
                            <TableCell width={`${100 / array.length}%`}>
                              {sentenceCase(key)}
                            </TableCell>
                          ))}
                        </TableHead>
                        <TableBody>
                          {value.map((item, i) => (
                            <TableRow key={i}>
                              {Object.keys(item).map((key) => (
                                <TableCell>{item[key] || '-'}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                }

                return <DescriptionItem label={label} value={value || '-'} />;
              })}
            </DescriptionList>
          </Section>
        </SidePage.NewContent>
        <SidePage.Sidebar
          actions={
            <Actions
              actions={data?.actions}
              data={data}
              type={TYPE_CONVERSION}
              onDuplicate={handleDuplicate}
              onEdit={handleEdit}
              onRemove={handleRemove}
            />
          }
        >
          <Metadata data={data} />
        </SidePage.Sidebar>
        {childComponent}
      </SidePage.NewBody>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
    </SidePage>
  );
};

export default IntegrationConversionDetails;
